/**
 * Check-in Evolution Analyzer
 * Compares current week vs previous week to highlight improvements
 * Uses supportive, motivational language to celebrate progress
 */

/**
 * Analyze evolution between two weeks
 * @param {Array} currentWeek - Last 7 days of data
 * @param {Array} previousWeek - Previous 7 days of data
 * @returns {Object} - Evolution insights and messages
 */
export const analyzeEvolution = (currentWeek, previousWeek) => {
  const improvements = [];
  const maintained = [];
  const declines = [];
  
  // Calculate metrics for both weeks
  const currentMetrics = calculateWeekMetrics(currentWeek);
  const previousMetrics = calculateWeekMetrics(previousWeek);
  
  // Compare sleep
  const sleepEvolution = compareMetric(
    currentMetrics.sleep,
    previousMetrics.sleep,
    'sleep'
  );
  categorizeEvolution(sleepEvolution, improvements, maintained, declines);
  
  // Compare hydration
  const hydrationEvolution = compareMetric(
    currentMetrics.hydration,
    previousMetrics.hydration,
    'hydration'
  );
  categorizeEvolution(hydrationEvolution, improvements, maintained, declines);
  
  // Compare activity
  const activityEvolution = compareMetric(
    currentMetrics.activity,
    previousMetrics.activity,
    'activity'
  );
  categorizeEvolution(activityEvolution, improvements, maintained, declines);
  
  // Compare mood
  const moodEvolution = compareMood(
    currentMetrics.mood,
    previousMetrics.mood
  );
  if (moodEvolution) {
    categorizeEvolution(moodEvolution, improvements, maintained, declines);
  }
  
  // Compare overall completion
  const completionEvolution = compareMetric(
    currentMetrics.completion,
    previousMetrics.completion,
    'completion'
  );
  categorizeEvolution(completionEvolution, improvements, maintained, declines);
  
  // Generate overall summary
  const summary = generateSummary(improvements, maintained, declines);
  
  return {
    improvements,
    maintained,
    declines,
    summary,
    currentMetrics,
    previousMetrics,
    hasProgress: improvements.length > 0
  };
};

/**
 * Calculate metrics for a week
 */
const calculateWeekMetrics = (weekData) => {
  const sleepDays = weekData.filter(d => d.sleep === true).length;
  const hydrationDays = weekData.filter(d => d.hydration === true).length;
  const activityDays = weekData.filter(d => d.activity === true).length;
  const completeDays = weekData.filter(d => 
    d.sleep && d.hydration && d.activity
  ).length;
  
  const goodMoodDays = weekData.filter(d => 
    d.mood === 'good' || d.feeling === 'good'
  ).length;
  const tiredDays = weekData.filter(d => 
    d.mood === 'tired' || d.feeling === 'tired'
  ).length;
  
  return {
    sleep: { days: sleepDays, rate: (sleepDays / weekData.length) * 100 },
    hydration: { days: hydrationDays, rate: (hydrationDays / weekData.length) * 100 },
    activity: { days: activityDays, rate: (activityDays / weekData.length) * 100 },
    completion: { days: completeDays, rate: (completeDays / weekData.length) * 100 },
    mood: {
      good: goodMoodDays,
      tired: tiredDays,
      goodRate: (goodMoodDays / weekData.length) * 100
    }
  };
};

/**
 * Compare a metric between two weeks
 */
const compareMetric = (current, previous, type) => {
  const difference = current.rate - previous.rate;
  const absoluteDiff = Math.abs(difference);
  
  // Determine trend
  let trend = 'maintained';
  if (difference > 15) trend = 'improved';
  else if (difference < -15) trend = 'declined';
  
  // Generate message
  const messages = getEvolutionMessages(type, trend, current, previous);
  
  return {
    type,
    trend,
    difference,
    current: current.days,
    previous: previous.days,
    message: messages.main,
    detail: messages.detail,
    emoji: messages.emoji,
    improvement: difference
  };
};

/**
 * Compare mood evolution
 */
const compareMood = (current, previous) => {
  const tiredDiff = previous.tired - current.tired;
  const goodDiff = current.good - previous.good;
  
  if (tiredDiff >= 2) {
    return {
      type: 'mood',
      trend: 'improved',
      message: `You felt tired less often this week`,
      detail: `${tiredDiff} fewer tired days compared to last week`,
      emoji: '😊',
      improvement: tiredDiff
    };
  }
  
  if (goodDiff >= 2) {
    return {
      type: 'mood',
      trend: 'improved',
      message: 'You felt good more often this week',
      detail: `${goodDiff} more good mood days`,
      emoji: '🌟',
      improvement: goodDiff
    };
  }
  
  return null;
};

/**
 * Get evolution messages based on type and trend
 */
const getEvolutionMessages = (type, trend, current, previous) => {
  const messages = {
    sleep: {
      improved: {
        main: 'Your sleep consistency is improving',
        detail: `${current.days} out of 7 days (up from ${previous.days} last week)`,
        emoji: '🌙'
      },
      maintained: {
        main: 'Your sleep remains consistent',
        detail: `${current.days} out of 7 days`,
        emoji: '😴'
      },
      declined: {
        main: 'Sleep has been less consistent',
        detail: `Let's aim for ${previous.days + 1} nights this week`,
        emoji: '🌙'
      }
    },
    hydration: {
      improved: {
        main: 'Your hydration habits are improving',
        detail: `${current.days} out of 7 days (up from ${previous.days} last week)`,
        emoji: '💧'
      },
      maintained: {
        main: 'You\'re staying well hydrated',
        detail: `${current.days} out of 7 days`,
        emoji: '💧'
      },
      declined: {
        main: 'Hydration could use more attention',
        detail: `Try keeping water nearby throughout the day`,
        emoji: '💧'
      }
    },
    activity: {
      improved: {
        main: 'You\'re moving more this week',
        detail: `${current.days} out of 7 days (up from ${previous.days} last week)`,
        emoji: '🏃'
      },
      maintained: {
        main: 'You\'re keeping up with movement',
        detail: `${current.days} out of 7 days`,
        emoji: '💪'
      },
      declined: {
        main: 'Movement has been less frequent',
        detail: `Even short walks make a difference`,
        emoji: '🚶'
      }
    },
    completion: {
      improved: {
        main: 'You\'re tracking more consistently',
        detail: `${current.days} complete days (up from ${previous.days} last week)`,
        emoji: '✨'
      },
      maintained: {
        main: 'Your tracking consistency is solid',
        detail: `${current.days} complete days`,
        emoji: '⭐'
      },
      declined: {
        main: 'Let\'s build back consistency',
        detail: `Small steps — try checking in 3-4 days this week`,
        emoji: '🌱'
      }
    }
  };
  
  return messages[type][trend];
};

/**
 * Categorize evolution into appropriate array
 */
const categorizeEvolution = (evolution, improvements, maintained, declines) => {
  if (evolution.trend === 'improved') {
    improvements.push(evolution);
  } else if (evolution.trend === 'maintained') {
    maintained.push(evolution);
  } else {
    declines.push(evolution);
  }
};

/**
 * Generate overall summary message
 */
const generateSummary = (improvements, maintained, declines) => {
  const totalChanges = improvements.length + maintained.length + declines.length;
  
  if (improvements.length >= 3) {
    return {
      text: 'Amazing progress this week! Multiple habits are improving.',
      type: 'excellent',
      emoji: '🎉'
    };
  }
  
  if (improvements.length >= 2) {
    return {
      text: 'Great work! You\'re making real progress.',
      type: 'good',
      emoji: '🌟'
    };
  }
  
  if (improvements.length >= 1) {
    return {
      text: 'Nice! You\'re moving in the right direction.',
      type: 'positive',
      emoji: '👍'
    };
  }
  
  if (maintained.length >= 3) {
    return {
      text: 'You\'re maintaining good consistency.',
      type: 'steady',
      emoji: '💪'
    };
  }
  
  if (declines.length >= 2) {
    return {
      text: 'It\'s been a challenging week. Tomorrow is a fresh start.',
      type: 'encouraging',
      emoji: '🌱'
    };
  }
  
  return {
    text: 'Keep going — every check-in helps you understand your patterns.',
    type: 'neutral',
    emoji: '📊'
  };
};

/**
 * Generate mock data for 2 weeks (for testing)
 */
export const generateMockTwoWeeks = () => {
  const generateWeek = (improvementBias = 0) => {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const rand = Math.random() + improvementBias;
      week.push({
        sleep: rand > 0.4,
        hydration: rand > 0.5,
        activity: rand > 0.45,
        mood: rand > 0.6 ? 'good' : (rand < 0.3 ? 'tired' : 'okay'),
        feeling: rand > 0.6 ? 'good' : (rand < 0.3 ? 'tired' : 'okay')
      });
    }
    return week;
  };
  
  // Previous week with lower rates
  const previousWeek = generateWeek(0);
  
  // Current week with slight improvement
  const currentWeek = generateWeek(0.15);
  
  return { currentWeek, previousWeek };
};

/**
 * Main export function
 */
export const generateEvolutionInsights = (realData = null) => {
  let currentWeek, previousWeek;
  
  if (realData) {
    currentWeek = realData.currentWeek;
    previousWeek = realData.previousWeek;
  } else {
    const mockData = generateMockTwoWeeks();
    currentWeek = mockData.currentWeek;
    previousWeek = mockData.previousWeek;
  }
  
  return analyzeEvolution(currentWeek, previousWeek);
};

export default {
  analyzeEvolution,
  generateEvolutionInsights,
  generateMockTwoWeeks
};
